/** Planes con IA (endpoint de Argos) sobre sitios reales ya filtrados; si la IA falla, planes calculados sin IA. */

import type { NearbyPlace, SocialContext, UserKit } from "./types";
import type { PlanCard, SettingPref } from "./plans";
import { cardFitsFilters, recommendPlans } from "./plans";
import { aiFailureMessage, askArgosAi, type AiFailure } from "./aiClient";
import { estimatedCostEur, fitsBudget, fitsTime, formatBudget, formatMinutes } from "./planFilters";
import type { TravelMode } from "./plans";

function whoLabel(social: SocialContext): string {
  switch (social) {
    case "pareja":
      return "en pareja";
    case "amigos":
      return "con amigos";
    case "familia":
    case "familia_extendida":
      return "en familia";
    case "colegas":
      return "con colegas";
    case "ninos":
      return "con peques";
    case "mascotas":
      return "con mascota";
    case "networking":
      return "para conocer gente";
    default:
      return "solo";
  }
}

function parsePlanLines(
  text: string,
): Array<{ title: string; detail?: string; minutes?: number; placeName?: string }> {
  const lines = String(text || "")
    .split(/\r?\n/)
    .map((line) => line.replace(/^[\s*\-\d.)]+/, "").trim())
    .filter((line) => line.length > 4);
  const out: Array<{ title: string; detail?: string; minutes?: number; placeName?: string }> = [];
  for (const line of lines) {
    const parts = line.split("|").map((p) => p.trim());
    if (parts.length >= 2) {
      const minutes = Number(parts[2]?.replace(/[^\d]/g, "") || parts.find((p) => /^\d{2,3}$/.test(p)));
      out.push({
        title: parts[0].slice(0, 120),
        detail: parts[1]?.slice(0, 180) || undefined,
        minutes: Number.isFinite(minutes) && minutes > 0 ? Math.min(240, minutes) : undefined,
        placeName: parts[3]?.slice(0, 80) || undefined,
      });
    } else if (!/^(aquí|te propongo|opciones)/i.test(line)) {
      out.push({ title: line.slice(0, 120) });
    }
  }
  return out.slice(0, 10);
}

async function askPlansAi(prompt: string): Promise<{ lines: ReturnType<typeof parsePlanLines>; failure?: AiFailure }> {
  const result = await askArgosAi(
    [
      {
        role: "system",
        content:
          "Eres Hermes, guía de planes locales en España. Responde SOLO líneas con este formato: título | detalle corto | minutos | nombre_lugar. Usa únicamente sitios del catálogo que te doy (copia el nombre exacto), respeta interior/exterior, compañía, presupuesto y tiempo. NUNCA inventes sitios ni escribas «por tu cuenta». Sin introducción.",
      },
      { role: "user", content: prompt },
    ],
    { timeoutMs: 25000, temperature: 0.4 },
  );
  if (!result.ok) return { lines: [], failure: result.reason ?? "unavailable" };
  return { lines: parsePlanLines(result.text) };
}

function findPlace(
  line: { title: string; placeName?: string },
  places: NearbyPlace[],
): NearbyPlace | null {
  const needle = (line.placeName || line.title).toLowerCase().trim();
  const title = line.title.toLowerCase();
  if (!needle) return null;
  return (
    places.find((p) => p.name.toLowerCase() === needle) ||
    places.find((p) => needle.includes(p.name.toLowerCase()) || title.includes(p.name.toLowerCase())) ||
    places.find((p) => needle.length >= 6 && p.name.toLowerCase().includes(needle.slice(0, 18))) ||
    null
  );
}

function attachMap(
  line: { title: string; detail?: string; minutes?: number; placeName?: string },
  places: NearbyPlace[],
  freeMinutes: number,
): PlanCard & { matched: NearbyPlace | null } {
  const match = findPlace(line, places);
  const minutes = Math.max(10, Math.min(freeMinutes, line.minutes ?? 60));
  return {
    title: line.title.replace(/\s*por tu cuenta\s*/gi, " ").replace(/\s+/g, " ").trim(),
    detail: (line.detail || "Plan cerca de ti").replace(/\s*por tu cuenta\s*/gi, " ").trim(),
    minutes,
    map: match ? { lat: match.latitude, lon: match.longitude } : undefined,
    placeName: match?.name || line.placeName,
    price: match ? estimatedCostEur(match) : undefined,
    matched: match,
  };
}

export type PlansAiInput = {
  mood: number;
  social: SocialContext;
  rainy?: boolean;
  /** @deprecated usa maxBudgetEur = 0. */
  freeOnly?: boolean;
  /** Presupuesto máximo por persona (€). null/undefined = sin límite. */
  maxBudgetEur?: number | null;
  setting?: SettingPref;
  /** Sitios ya filtrados por presupuesto y tiempo (los que no encajan se descartan aquí también). */
  places: NearbyPlace[];
  kit: UserKit;
  hour?: number;
  minutes?: number;
  /** Cómo se desplaza (para saber si da tiempo a ir y volver). Por defecto, a pie. */
  transporte?: TravelMode;
  origin?: { latitude: number; longitude: number } | null;
  /** true = sin IA (función premium bloqueada): solo el recomendador local. */
  noAi?: boolean;
};

export type PlansAiResult = {
  cards: PlanCard[];
  /** Qué pasó con la IA: usada, no pedida, o por qué falló (mensaje listo para mostrar). */
  ai: "used" | "skipped" | "failed";
  note?: string;
};

/** Construye el texto que se envía a la IA (exportado para tests). */
export function buildPlansPrompt(input: PlansAiInput, places: NearbyPlace[]): string {
  const minutes = Math.round(input.minutes || 90);
  const budgetValue = input.maxBudgetEur != null ? input.maxBudgetEur : input.freeOnly ? 0 : null;
  const hobbies = input.kit.hobbies.slice(0, 8).join(", ") || "ninguno anotado";
  const budget =
    budgetValue == null
      ? "presupuesto sin límite"
      : budgetValue === 0
        ? "SOLO planes gratis (0 €)"
        : `presupuesto máximo ${formatBudget(budgetValue)} por persona`;
  const setting =
    input.setting === "indoor"
      ? "solo INTERIORES"
      : input.setting === "outdoor"
        ? "solo EXTERIORES"
        : "da igual interior o exterior";
  const settingRule =
    input.setting === "indoor"
      ? "PROHIBIDO parques, plazas, miradores, monumentos al aire libre, montañas, puentes o mercadillos."
      : input.setting === "outdoor"
        ? "PROHIBIDO cines, boleras, discotecas, supermercados, museos y bibliotecas."
        : "";
  const catalog = places.length
    ? places
        .slice(0, 30)
        .map((p) => {
          const cost = estimatedCostEur(p);
          return `${p.name} (${p.kind}${p.distanceMeters != null ? `, ${p.distanceMeters} m` : ""}, ${cost ? `~${cost} €` : "gratis"})`;
        })
        .join("; ")
    : "";
  return `Propón hasta 6 planes para ${formatMinutes(minutes)} de tiempo libre en total (incluida la ida y la vuelta), ${whoLabel(input.social)}. Filtros obligatorios: ${budget}; ${setting}. ${settingRule} Hobbies: ${hobbies}. Elige SOLO entre estos sitios reales: ${catalog}. Cada línea: título con el nombre del sitio | por qué encaja | minutos de estancia (máximo ${minutes}) | nombre exacto del sitio.`;
}

export async function recommendPlansSmartDetailed(input: PlansAiInput): Promise<PlansAiResult> {
  const settingPref: SettingPref = input.setting ?? "any";
  const budgetValue = input.maxBudgetEur != null ? input.maxBudgetEur : input.freeOnly ? 0 : null;
  const freeMinutes = Math.max(15, Math.round(input.minutes || 90));
  const places = input.places.filter(
    (p) => fitsBudget(p, budgetValue) && fitsTime(p, freeMinutes, input.transporte ?? "walk"),
  );
  const filterOpts = { social: input.social, freeOnly: budgetValue === 0, setting: settingPref };

  const fits = (card: PlanCard, matched?: NearbyPlace | null): boolean => {
    if (/por tu cuenta/i.test(card.title)) return false;
    if (matched && !fitsBudget(matched, budgetValue)) return false;
    if (budgetValue != null && card.price != null && card.price > budgetValue) return false;
    return cardFitsFilters(card, places, filterOpts);
  };

  const base = recommendPlans({ ...input, freeOnly: budgetValue === 0, places })
    .map((card) => {
      const matched = card.placeName ? places.find((p) => p.name === card.placeName) || null : null;
      return {
        ...card,
        title: card.title.replace(/\s*por tu cuenta\s*/gi, " ").replace(/\s+/g, " ").trim(),
        detail: card.detail.replace(/\s*por tu cuenta\s*/gi, " ").trim(),
        minutes: Math.max(10, Math.min(freeMinutes, card.minutes)),
        price: matched ? estimatedCostEur(matched) : card.price,
      };
    })
    .filter((card) => fits(card, card.placeName ? places.find((p) => p.name === card.placeName) : null));

  // Sin sitios reales la IA solo inventaría: usamos el recomendador local.
  if (input.noAi || !places.length) return { cards: base, ai: "skipped" };

  const { lines, failure } = await askPlansAi(buildPlansPrompt(input, places));
  if (failure) return { cards: base, ai: "failed", note: aiFailureMessage(failure) };

  const aiCards = lines
    .map((line) => attachMap(line, places, freeMinutes))
    // Solo sitios que existen en el catálogo (la IA a veces se los inventa).
    .filter((card) => card.matched && fits(card, card.matched))
    .map(({ matched: _matched, ...card }) => card);
  const uniq = new Map<string, PlanCard>();
  for (const card of [...aiCards, ...base]) {
    const key = card.title.toLowerCase();
    if (!uniq.has(key)) uniq.set(key, card);
  }
  return { cards: [...uniq.values()].slice(0, 24), ai: aiCards.length ? "used" : "failed", note: aiCards.length ? undefined : "La IA no propuso nada que encaje con tus filtros; te muestro planes calculados sin IA." };
}

/** Compatibilidad: solo las tarjetas. */
export async function recommendPlansSmart(input: PlansAiInput): Promise<PlanCard[]> {
  return (await recommendPlansSmartDetailed(input)).cards;
}
